import type { ProductInfoProps } from '@components/ProductInfo';
import type { ProductListItem } from '@providers/ProductListProvider/types';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import { objectKeys } from '@utils';
import { useMemo } from 'react';

import type { ProductCTAPropsMapSettings } from './connectCTAButton';
import { useConnectCTAButton } from './connectCTAButton';
import type { InventoryStatusPropsMapSettings } from './connectInventoryStatus';
import { useConnectInventoryStatus } from './connectInventoryStatus';
import type { ProductPricePropsMapSettings } from './connectProductPrice';
import { useConnectProductPrice } from './connectProductPrice';
import type { ProductRatingPropsMapSettings } from './connectProductRating';
import { useConnectProductRating } from './connectProductRating';
import type { ProductSwatchesPropsMapSettings } from './connectProductSwatches';
import { useConnectProductSwatches } from './connectProductSwatches';
import type { ProductTitlePropsMapSettings } from './connectProductTitle';
import { useConnectProductTitle } from './connectProductTitle';
import type { ProductVendorPropsMapSettings } from './connectProductVendor';
import { useConnectProductVendor } from './connectProductVendor';

export type ProductInfoPropsMapSettings = Pick<
  ProductInfoProps,
  'textAlign' | 'orders' | 'padding'
> & {
  elements: {
    title?: ProductTitlePropsMapSettings;
    price?: ProductPricePropsMapSettings;
    vendor?: ProductVendorPropsMapSettings;
    inventoryStatus: InventoryStatusPropsMapSettings;
    swatches?: ProductSwatchesPropsMapSettings;
    cta?: ProductCTAPropsMapSettings;
    rating?: ProductRatingPropsMapSettings;
  };
};

export const useConnectProductInfo = (product: ProductListItem) => {
  const { productInfo: infoSettings } = useProductItemThemeSettings();
  const { props: titleElementProps } = useConnectProductTitle();
  const { props: priceElementProps } = useConnectProductPrice();
  const { props: vendorElementProps } = useConnectProductVendor();
  const { props: inventoryStatusElementProps } = useConnectInventoryStatus();
  const { props: productSwatchesProps } = useConnectProductSwatches('info', product);
  const { props: productRatingProps } = useConnectProductRating('info');
  const { props: productCTAProps } = useConnectCTAButton('info', product);

  const props = useMemo(() => {
    const { padding, textAlign } = infoSettings || {};

    const productInfoProps: ProductInfoPropsMapSettings = {
      elements: {
        title: titleElementProps,
        price: priceElementProps,
        vendor: vendorElementProps,
        inventoryStatus: inventoryStatusElementProps,
        swatches: productSwatchesProps,
        cta: productCTAProps,
        rating: productRatingProps,
      },
      padding,
      textAlign,
      orders: objectKeys(infoSettings?.elements || {}),
    };

    return productInfoProps;
  }, [
    infoSettings,
    titleElementProps,
    priceElementProps,
    vendorElementProps,
    productSwatchesProps,
    productCTAProps,
    productRatingProps,
    inventoryStatusElementProps,
  ]);

  return {
    props,
  };
};
